import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'motion/react';
import { useAuthContext } from '@/components/auth';
import {
  Plus,
  Library,
  PanelLeftClose,
  PanelLeft,
  MessageCircle,
  Trash2,
  Loader2,
  MessageSquare,
  RefreshCw,
  Sparkles,
  ClipboardList,
  MoreHorizontal,
  Shield,
  Camera,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useConversationList,
  useDeleteConversation,
  type ConversationSummary,
} from '@/hooks/useConversationHistory';
import { useNewChat } from '@/hooks/useNewChat';
import { useCurrentChild, useChildList } from '@/hooks/useChild';
import { useProfile } from '@/hooks/useProfile';
import { useChatStore } from '@/stores';
import { AbandonConversationDialog } from './AbandonConversationDialog';
import { cn } from '@/lib/utils';
import { ChildProfileHeader } from './ChildProfileHeader';
import { UserProfileFooter } from './UserProfileFooter';
import { ChildSwitchDialog } from '@/components/child';
import type { AvatarKey } from '@/constants/avatars';
import type { ChildGrade } from '@/hooks/useChild';
import { conversationKeys } from '@/hooks/useConversationHistory';
import { taskKeys } from '@/hooks/useTask';
import { useQueryClient } from '@tanstack/react-query';

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  onCloseMobile?: () => void;
  className?: string;
}

/**
 * Menu items configuration (labels are i18n keys)
 * - home: Home/New conversation entry
 * - scan: Scan homework for quick task generation
 * - tasks: Task list
 * - admin (dev only): Admin dashboard
 */
const navItemConfigs: Array<{ id: string; labelKey: string; icon: React.ReactNode; path: string; devOnly?: boolean }> = [
  {
    id: 'home',
    labelKey: 'sidebar:menu.home',
    icon: <Sparkles className="h-[22px] w-[22px]" />,
    path: '/',
  },
  {
    id: 'scan',
    labelKey: 'sidebar:menu.scan',
    icon: <Camera className="h-[22px] w-[22px]" />,
    path: '/scan',
  },
  {
    id: 'chat',
    labelKey: 'sidebar:menu.chat',
    icon: <MessageCircle className="h-[22px] w-[22px]" />,
    path: '/chat',
  },
  {
    id: 'tasks',
    labelKey: 'sidebar:menu.tasks',
    icon: <Library className="h-[22px] w-[22px]" />,
    path: '/tasks',
  },
  {
    id: 'admin',
    labelKey: 'sidebar:menu.admin',
    icon: <Shield className="h-[22px] w-[22px]" />,
    path: '/admin',
    devOnly: true,
  },
];

/**
 * Status color configuration
 */
const statusConfig: Record<string, { textColor: string }> = {
  idle: { textColor: '#6B7280' },
  clarify_profile: { textColor: '#92400E' },
  clarify_content: { textColor: '#1E40AF' },
  ready: { textColor: '#065F46' },
  generating: { textColor: '#5B21B6' },
  complete: { textColor: '#059669' },
  reject: { textColor: '#991B1B' },
  error: { textColor: '#991B1B' },
};

/**
 * Format conversation time (with i18n support)
 * - Today: HH:mm
 * - Yesterday: "Yesterday"
 * - This week: Weekday name
 * - Earlier: MM/DD
 */
function formatConversationTime(dateStr: string, t: (key: string, options?: { returnObjects?: boolean }) => string | string[]): string {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  if (date >= today) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } else if (date >= yesterday) {
    return t('sidebar:time.yesterday') as string;
  } else if (date >= weekAgo) {
    const weekdays = t('sidebar:time.weekdays', { returnObjects: true }) as string[];
    return weekdays[date.getDay()];
  } else {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}`;
  }
}

/**
 * ConversationItem - Single conversation row
 *
 * ChatGPT-style design (tablet + extensible):
 * - Icon + Title + Status badge + Message count + Time
 * - Right side: Task button (completed) + More menu (...) + Arrow
 * - More menu: Delete (future: rename, share, archive)
 * - `...` button always visible (faded), supports tablet tap
 */
function ConversationItem({
  conversation,
  isActive,
  collapsed,
  onDelete,
  onViewTask,
  isDeleting,
  t,
}: {
  conversation: ConversationSummary;
  isActive: boolean;
  collapsed: boolean;
  onDelete: () => void;
  onViewTask?: () => void;
  isDeleting: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const status = statusConfig[conversation.status] || statusConfig.clarify_content;
  const hasTask = conversation.status === 'complete' && conversation.taskId;
  const timeStr = formatConversationTime(conversation.createdAt, t as (key: string, options?: { returnObjects?: boolean }) => string | string[]);

  return (
    <div
      className={cn(
        'group relative mx-2 flex cursor-pointer items-center rounded-lg px-3 py-2.5 transition-colors',
        isActive ? 'bg-gray-100' : 'hover:bg-gray-50',
        collapsed && 'mx-0 justify-center px-0'
      )}
    >
      {/* Icon */}
      <div className={cn('shrink-0', !collapsed && 'mr-3')}>
        <MessageCircle
          className={cn('h-[18px] w-[18px]', isDeleting ? 'text-gray-300' : 'text-gray-500')}
        />
      </div>

      {!collapsed && (
        <>
          {/* Content */}
          <div className="min-w-0 flex-1">
            {/* 标题行 */}
            <p className={cn('truncate text-sm', isDeleting ? 'text-gray-400' : 'text-gray-800')}>
              {conversation.title}
            </p>
            {/* Meta 行：状态 + 时间 */}
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px]">
              {/* Status (short text with color) */}
              <span className="font-medium" style={{ color: status.textColor }}>
                {t(`sidebar:statusShort.${conversation.status}`)}
              </span>
              <span className="text-gray-300">·</span>
              {/* Time */}
              <span className="text-gray-400">{timeStr}</span>
            </div>
          </div>

          {/* Right Side: Task button + More menu */}
          <div className="ml-1 flex shrink-0 items-center">
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            ) : (
              <>
                {/* Task button for completed conversations */}
                {hasTask && onViewTask && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onViewTask();
                    }}
                    className="mr-0.5 flex h-6 w-6 items-center justify-center rounded bg-violet-50 text-violet-600 transition-colors hover:bg-violet-100"
                    title={t('sidebar:actions.viewTask')}
                  >
                    <ClipboardList className="h-3.5 w-3.5" />
                  </button>
                )}

                {/* More menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      className="flex h-6 w-6 items-center justify-center rounded text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-500"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="bottom" align="end" sideOffset={4} className="w-36">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                      }}
                      className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('sidebar:actions.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Sidebar - 左侧导航栏
 *
 * 与 App 端对齐的设计：
 * - 顶部：当前孩子信息 (ChildProfileHeader)
 * - 菜单项：StudyJoyful + 我的任务（无"当前"标签）
 * - 最近对话：标题 + 状态标签 + 消息数（按 childId 过滤）
 * - 底部：用户信息 (UserProfileFooter)
 */
export function Sidebar({ collapsed = false, onToggle, onCloseMobile, className }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConversationSummary | null>(null);
  const [childSwitchOpen, setChildSwitchOpen] = useState(false);

  // Build nav items (filter dev-only in production)
  const navItems = navItemConfigs.filter(item => !item.devOnly || import.meta.env.DEV);

  // Auth0 user info (for email)
  const { user: authUser } = useAuthContext();

  // Profile data (for name)
  const { data: profileData } = useProfile();
  const profile = profileData?.data;

  // Current child
  const { data: childData } = useCurrentChild();
  const currentChild = childData?.data;

  // All children (to check if can switch)
  const { data: childrenData, isLoading: isChildListLoading } = useChildList();
  const childrenCount = childrenData?.data?.length || 0;
  // Allow switching if still loading (switcher has its own loading state) or if multiple children confirmed
  const canSwitchChild = isChildListLoading || childrenCount > 1;

  // Chat store for reset function
  const { resetFn } = useChatStore();

  // Unified new chat logic (with abandon confirmation)
  const {
    startNewChat,
    confirmAbandon,
    cancelAbandon,
    showAbandonDialog,
  } = useNewChat({ onCloseMobile });

  // Fetch conversations from API - filtered by current child
  const {
    data: conversationsData,
    isLoading,
    error,
    refetch,
  } = useConversationList({
    pageSize: 10,
    includeCompleted: true,
    childId: currentChild?.id,
  });

  // Delete mutation
  const { mutate: deleteConversationMutation } = useDeleteConversation();

  const conversations = conversationsData?.data || [];

  // Check if nav item is active
  // Special case: /tasks should also be active when viewing /task/:id or /task/:id/report
  const isActive = (path: string) => {
    if (path === '/tasks') {
      return location.pathname === '/tasks' || location.pathname.startsWith('/task/');
    }
    return location.pathname === path;
  };

  // Handle conversation click
  const handleConversationClick = useCallback(() => {
    onCloseMobile?.();
  }, [onCloseMobile]);

  // Handle non-home nav item click (tasks, admin, etc.)
  const handleNavClick = useCallback(() => {
    onCloseMobile?.();
  }, [onCloseMobile]);

  // Handle delete conversation - 打开确认对话框
  const handleDeleteClick = useCallback((conv: ConversationSummary) => {
    setDeleteTarget(conv);
  }, []);

  // Handle delete confirm - 确认删除
  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;

    setDeletingId(deleteTarget.id);
    deleteConversationMutation(deleteTarget.id, {
      onSettled: () => {
        setDeletingId(null);
        setDeleteTarget(null);
      },
      onSuccess: () => {
        // If deleting current conversation, navigate to home
        if (location.pathname === `/c/${deleteTarget.id}`) {
          navigate('/');
        }
      },
    });
  }, [deleteTarget, deleteConversationMutation, location.pathname, navigate]);

  // Handle go to profile
  const handleGoToProfile = useCallback(() => {
    navigate('/profile');
    onCloseMobile?.();
  }, [navigate, onCloseMobile]);

  // Handle child switch
  const handleSwitchChild = useCallback(() => {
    if (canSwitchChild) {
      setChildSwitchOpen(true);
    } else {
      // Only one child, go to profile to manage
      handleGoToProfile();
    }
  }, [canSwitchChild, handleGoToProfile]);

  // Handle child switched - refresh related queries
  const handleChildSwitched = useCallback(() => {
    // Clear current conversation when child changes
    if (resetFn) {
      resetFn();
    }
    // Refresh conversation and task lists (they'll use new childId)
    queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    // Navigate to home if not already there
    if (location.pathname !== '/') {
      navigate('/');
    }
    onCloseMobile?.();
  }, [resetFn, queryClient, location.pathname, navigate, onCloseMobile]);

  // User info for footer
  const userName = profile?.name || authUser?.name || t('sidebar:fallback.defaultUser');
  const userEmail = authUser?.email || profile?.email;

  return (
    <TooltipProvider delayDuration={0}>
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 72 : 280 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className={cn('flex h-full flex-col overflow-hidden bg-white', className)}
      >
        {/* Header: Child Profile */}
        <AnimatePresence mode="wait">
          {currentChild ? (
            <ChildProfileHeader
              nickname={currentChild.nickname}
              avatar={currentChild.avatar as AvatarKey}
              grade={currentChild.grade as ChildGrade}
              canSwitch={canSwitchChild}
              onPress={handleSwitchChild}
              collapsed={collapsed}
              onToggleSidebar={onToggle}
            />
          ) : (
            // Fallback: 无孩子档案时显示品牌信息（理论上不应该出现）
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn(
                'flex items-center gap-3 border-b border-gray-100 px-4 py-4',
                collapsed && 'flex-col justify-center'
              )}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-violet-500">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right">StudyJoyful</TooltipContent>}
              </Tooltip>
              {!collapsed && (
                <p className="min-w-0 flex-1 text-base font-semibold text-gray-900">StudyJoyful</p>
              )}
              {onToggle && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={onToggle}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    >
                      {collapsed ? (
                        <PanelLeft className="h-5 w-5" />
                      ) : (
                        <PanelLeftClose className="h-5 w-5" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {collapsed ? t('sidebar:actions.expand') : t('sidebar:actions.collapse')}
                  </TooltipContent>
                </Tooltip>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Scrollable Content */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {/* Navigation Menu */}
          <div className="py-3">
            {navItems.map((item) => {
              const active = isActive(item.path);
              const label = t(item.labelKey);

              const content = (
                <div
                  className={cn(
                    'mx-2 flex items-center gap-3 rounded-lg px-4 py-3 transition-colors',
                    active ? 'bg-gray-100' : 'hover:bg-gray-50',
                    collapsed && 'justify-center px-0'
                  )}
                >
                  <span
                    className={cn('shrink-0', active ? 'text-gray-900' : 'text-gray-500')}
                  >
                    {item.icon}
                  </span>
                  {!collapsed && (
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-base',
                        active ? 'font-semibold text-gray-900' : 'text-gray-700'
                      )}
                    >
                      {label}
                    </span>
                  )}
                </div>
              );

              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <NavLink to={item.path} onClick={handleNavClick}>
                      {content}
                    </NavLink>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right">{label}</TooltipContent>}
                </Tooltip>
              );
            })}
          </div>

          {/* Conversation History Section */}
          <div className="border-t border-gray-100 py-2">
            {/* Header - Title + Refresh button + New button */}
            <div className="flex items-center justify-between px-4 py-2">
              {!collapsed && (
                <div className="flex items-center gap-2">
                  <span className="shrink-0 text-xs font-medium text-gray-500">{t('sidebar:history.title')}</span>
                  {/* Refresh Button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => refetch()}
                        disabled={isLoading}
                        className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:text-gray-300"
                      >
                        <RefreshCw className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{t('sidebar:actions.refresh')}</TooltipContent>
                  </Tooltip>
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={startNewChat}
                    className={cn(
                      'flex shrink-0 items-center gap-0.5 rounded-full bg-violet-50 px-2 py-1 text-xs font-medium text-violet-600 transition-colors hover:bg-violet-100',
                      collapsed && 'mx-auto'
                    )}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {!collapsed && <span>{t('sidebar:history.new')}</span>}
                  </button>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right">{t('sidebar:history.newConversation')}</TooltipContent>}
              </Tooltip>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex flex-col items-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
                {!collapsed && <p className="mt-2 text-xs text-gray-400">{t('sidebar:loading')}</p>}
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className="flex flex-col items-center py-4">
                {!collapsed && (
                  <>
                    <p className="text-xs text-gray-400">{t('sidebar:error.failed')}</p>
                    <button
                      onClick={() => refetch()}
                      className="mt-1 flex items-center gap-1 text-xs text-violet-500 hover:text-violet-600"
                    >
                      <RefreshCw className="h-3 w-3" />
                      <span>{t('sidebar:actions.retry')}</span>
                    </button>
                  </>
                )}
                {collapsed && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => refetch()}
                        className="text-violet-500 hover:text-violet-600"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{t('sidebar:actions.reload')}</TooltipContent>
                  </Tooltip>
                )}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && conversations.length === 0 && (
              <div className="flex flex-col items-center py-6">
                <MessageSquare className="h-6 w-6 text-gray-200" />
                {!collapsed && <p className="mt-1 text-xs text-gray-400">{t('sidebar:history.empty')}</p>}
              </div>
            )}

            {/* Conversation List */}
            {!isLoading && !error && conversations.length > 0 && (
              <div className="space-y-0.5">
                {conversations.map((conv) => {
                  const isConvActive = location.pathname === `/c/${conv.id}`;
                  return (
                    <Tooltip key={conv.id}>
                      <TooltipTrigger asChild>
                        <NavLink to={`/c/${conv.id}`} onClick={handleConversationClick}>
                          <ConversationItem
                            conversation={conv}
                            isActive={isConvActive}
                            collapsed={collapsed}
                            onDelete={() => handleDeleteClick(conv)}
                            onViewTask={
                              conv.taskId
                                ? () => {
                                    navigate(`/task/${conv.taskId}`);
                                    onCloseMobile?.();
                                  }
                                : undefined
                            }
                            isDeleting={deletingId === conv.id}
                            t={t}
                          />
                        </NavLink>
                      </TooltipTrigger>
                      {collapsed && <TooltipContent side="right">{conv.title}</TooltipContent>}
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer: User Profile */}
        <UserProfileFooter
          name={userName}
          email={userEmail}
          onPress={handleGoToProfile}
          collapsed={collapsed}
        />
      </motion.aside>

      {/* Child Switch Dialog */}
      <ChildSwitchDialog
        open={childSwitchOpen}
        onOpenChange={setChildSwitchOpen}
        currentChildId={currentChild?.id}
        onSwitch={handleChildSwitched}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sidebar:delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('sidebar:delete.description', { title: deleteTarget?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>{t('sidebar:delete.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={!!deletingId}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {deletingId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('sidebar:delete.deleting')}
                </>
              ) : (
                t('sidebar:delete.confirm')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Abandon Conversation Dialog (from useNewChat) */}
      <AbandonConversationDialog
        open={showAbandonDialog}
        onConfirm={confirmAbandon}
        onCancel={cancelAbandon}
      />
    </TooltipProvider>
  );
}
